import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

import Loader from "../components/loader/loader";
import NotFound from "../pages/notFound/notFound";
import Layout from "../layout";

const Login = lazy(() => import("../pages/onboarding/Login"));
const ForgetPasswordInput = lazy(
  () => import("../pages/onboarding/ForgetPasswordInput"),
);
const SuperAdminDashboard = lazy(
  () => import("../pages/admin/dashboard/SuperAdminDashboard"),
);
const AdminDashboard = lazy(
  () => import("../pages/admin/dashboard/AdminDashboard"),
);
const ManagerDashboard = lazy(
  () => import("../pages/manager/mdashboard/ManagerDashboard"),
);

const Organization = lazy(
  () => import("../pages/admin/organization/Organization"),
);
const AddNewOrganization = lazy(
  () => import("../pages/admin/organization/AddNewOrganization"),
);
const OrganizationDetailView = lazy(
  () => import("../pages/admin/organization/OrganizationDetailView"),
);
const EditOrganization = lazy(
  () => import("../pages/admin/organization/EditOrganization"),
);

const Customers = lazy(() => import("../pages/admin/customer/Customers"));
const AddNewCustomer = lazy(
  () => import("../pages/admin/customer/AddNewCustomer"),
);
const CustomerDetailView = lazy(
  () => import("../pages/admin/customer/CustomerDetailView"),
);
const EditCustomer = lazy(() => import("../pages/admin/customer/EditCustomer"));

const ProgramManagers = lazy(
  () => import("../pages/admin/program_manager/ProgramManagers"),
);
const AddNewProgramManager = lazy(
  () => import("../pages/admin/program_manager/AddNewProgramManager"),
);
const ProgramManagerDetailView = lazy(
  () => import("../pages/admin/program_manager/ProgramManagerDetailView"),
);
const EditProgramManager = lazy(
  () => import("../pages/admin/program_manager/EditProgramManager"),
);

const Vendors = lazy(() => import("../pages/admin/vendor/Vendors"));
const AddNewVendor = lazy(() => import("../pages/admin/vendor/AddNewVendor"));
const VendorDetailView = lazy(
  () => import("../pages/admin/vendor/VendorDetailView"),
);
const EditVendor = lazy(() => import("../pages/admin/vendor/EditVendor"));

const Tiers = lazy(() => import("../pages/admin/vendor/tier/Tiers"));
const AddNewTier = lazy(() => import("../pages/admin/vendor/tier/AddNewTier"));
const EditTier = lazy(() => import("../pages/admin/vendor/tier/EditTier"));

const Services = lazy(() => import("../pages/admin/service/Services"));
const AddNewService = lazy(
  () => import("../pages/admin/service/AddNewService"),
);
const EditService = lazy(() => import("../pages/admin/service/EditService"));

const BusinessUsers = lazy(
  () => import("../pages/admin/bussiness_user/BusinessUsers"),
);
const AddNewBusinessUser = lazy(
  () => import("../pages/admin/bussiness_user/AddNewBusinessUser"),
);
const BusinessUserDetailView = lazy(
  () => import("../pages/admin/bussiness_user/BusinessUserDetailView"),
);
const EditBusinessUser = lazy(
  () => import("../pages/admin/bussiness_user/EditBusinessUser"),
);

const ExpenseManagement = lazy(
  () => import("../pages/admin/expense_management/ExpenseManagement"),
);
const ExpenseSubmissions = lazy(
  () => import("../pages/admin/expense_management/ExpenseSubmissions"),
);
const ReimbursementTemplates = lazy(
  () => import("../pages/admin/expense_management/ReimbursementTemplates"),
);
const ExpenseDetailView = lazy(
  () => import("../pages/admin/expense_management/ExpenseDetailView"),
);

const AnalyticsReports = lazy(
  () => import("../pages/admin/report&analytics/AnalyticsReports"),
);
const AnalyticsReportDetailView = lazy(
  () => import("../pages/admin/report&analytics/AnalyticsReportDetailView"),
);

const RoleAndPermissions = lazy(
  () => import("../pages/admin/role&permission/RoleAndPermissions"),
);
const AddRoleAndPermission = lazy(
  () => import("../pages/admin/role&permission/AddRoleAndPermission"),
);
const EditAddRoleAndPermission = lazy(
  () => import("../pages/admin/role&permission/EditAddRoleAndPermission"),
);
const SAMvideos = lazy(() => import("../pages/admin/SAMVideo/SAMvideos"));
const AddSAMVideo = lazy(() => import("../pages/admin/SAMVideo/AddSAMVideo"));
const EditSAMVideo = lazy(() => import("../pages/admin/SAMVideo/EditSAMVideo"));
const Settings = lazy(() => import("../pages/admin/setting/Settings"));
const Housing = lazy(() => import("../pages/admin/aibuddy_management/Housing"));
const AddNewHouse = lazy(() => import("../pages/admin/aibuddy_management/Addnewhouse"));
const EditHouse = lazy(() => import("../pages/admin/aibuddy_management/Edithouse"));
const HouseDetailView = lazy(() => import("../pages/admin/aibuddy_management/Housedetailview"));
const ManagerOnboardSteps = lazy(() => import("../pages/onboarding/ManagerOnboardSteps"));
const ManagerVendors = lazy(() => import("../pages/manager/mVendor/ManagerVendors"));
const ManagerVendorDetailView = lazy(() => import("../pages/manager/mVendor/ManagerVendorDetailView"));
const MyProfile = lazy(() => import("../pages/manager/profile/MyProfile"));

// manager route
const AssignedUsers = lazy(() => import("../pages/manager/assigned_user/AssignedUsers"));
const AssignedDetailView = lazy(() => import("../pages/manager/assigned_user/AssignedDetailView"));
const Communication = lazy(() => import("../pages/manager/communcation/Communication"));
const ChatsDetailView = lazy(() => import("../pages/manager/communcation/ChatsDetailView"));
const QueriesDetailView = lazy(() => import("../pages/manager/communcation/QueriesDetailView"));
const ManagerHousing = lazy(() => import("../pages/manager/manager_housing/ManagerHousing"));
const ServiceManager = lazy(() => import("../pages/manager/service_manager/ServiceManager"));
const ServiceManagerDetailView = lazy(() => import("../pages/manager/service_manager/ServiceManagerDetailView"));

//manager dashboard sirf tab accessible ho jab profile_completed true ho
const ManagerDashboardGuard = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  if (!user?.profile_completed) {
    return <Navigate to="/manager-onboarding-steps" replace />;
  }
  return children;
};

//onboarding sirf tab dikhao jab profile_completed false ho
const OnboardingGuard = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  if (user?.profile_completed) {
    return <Navigate to="/manager-dashboard" replace />;
  }
  return children;
};

const AppRoutes = () => {
  const { role, isLoggedIn, user } = useSelector((state) => state.auth);

  const adminRoutes = [
    { path: "/admin-dashboard", element:<SuperAdminDashboard />, layout: true },

    // Organization
    { path: "/organizations", element: <Organization />, layout: true },
    { path: "/organizations/add-organization", element: <AddNewOrganization />, layout: true },
    { path: "/organizations/organization-detail-view", element: <OrganizationDetailView />, layout: true },
    { path: "/organizations/edit-organization", element: <EditOrganization />, layout: true },

    // Customer
    { path: "/customers", element: <Customers />, layout: true },
    { path: "/customers/add-customer", element: <AddNewCustomer />, layout: true },
    { path: "/customers/customer-detail-view", element: <CustomerDetailView />, layout: true },
    { path: "/customers/edit-customer", element: <EditCustomer />, layout: true },

    // Program Manager
    { path: "/program-managers", element: <ProgramManagers />, layout: true },
    { path: "/program-managers/add-program-manager", element: <AddNewProgramManager />, layout: true },
    { path: "/program-managers/program-manager-detail-view", element: <ProgramManagerDetailView />, layout: true },
    { path: "/program-managers/edit-program-manager", element: <EditProgramManager />, layout: true },

    // Vendors
    { path: "/vendors", element: <Vendors />, layout: true },
    { path: "/vendors/add-vendor", element: <AddNewVendor />, layout: true },
    { path: "/vendors/vendor-detail-view", element: <VendorDetailView />, layout: true },
    { path: "/vendors/edit-vendor", element: <EditVendor />, layout: true },

    // Tier
    { path: "/tiers", element: <Tiers />, layout: true },
    { path: "/tiers/add-tier", element: <AddNewTier />, layout: true },
    { path: "/tiers/edit-tier", element: <EditTier />, layout: true },

    // Services
    { path: "/services", element: <Services />, layout: true },
    { path: "/services/add-service", element: <AddNewService />, layout: true },
    { path: "/services/edit-service", element: <EditService />, layout: true },

    // Business User
    { path: "/business-users", element: <BusinessUsers />, layout: true },
    { path: "/business-users/add-business-user", element: <AddNewBusinessUser />, layout: true },
    { path: "/business-users/business-user-detail-view", element: <BusinessUserDetailView />, layout: true },
    { path: "/business-users/edit-business-user", element: <EditBusinessUser />, layout: true },

    // Expense Management
    { path: "/expense-management", element: <ExpenseManagement />, layout: true },
    { path: "/expense-management/expense-submissions", element: <ExpenseSubmissions />, layout: true },
    { path: "/expense-management/reimbursement-templates", element: <ReimbursementTemplates />, layout: true },
    { path: "/expense-management/expense-detail-view", element: <ExpenseDetailView />, layout: true },

    // Analytics & Reports
    { path: "/analytics-reports", element: <AnalyticsReports />, layout: true },
    { path: "/analytics-reports/analytic-report-detail-view", element: <AnalyticsReportDetailView />, layout: true },

    // Roles And Permission
    { path: "/role-permissions", element: <RoleAndPermissions />, layout: true },
    { path: "/role-permissions/add-role-permission", element: <AddRoleAndPermission />, layout: true },
    { path: "/role-permissions/edit-role-permission", element: <EditAddRoleAndPermission />, layout: true },

    // SAM Video
    { path: "/sam-videos", element: <SAMvideos />, layout: true },
    { path: "/sam-videos/add-video", element: <AddSAMVideo />, layout: true },
    { path: "/sam-videos/edit-video", element: <EditSAMVideo />, layout: true },

    // Housing
    { path: "/housing", element: <Housing />, layout: true },
    { path: "/housing/add-house", element: <AddNewHouse />, layout: true },
    { path: "/housing/edit-house", element: <EditHouse />, layout: true },
    { path: "/housing/house-detail-view", element: <HouseDetailView />, layout: true },

    // Settings
    { path: "/settings", element: <Settings />, layout: true },
  ];

  const managerRoutes = [
    // Dashboard — profile_completed false hone pe onboarding pe redirect
    {
      path: "/manager-dashboard",
      element: (
        <ManagerDashboardGuard>
          <ManagerDashboard />
        </ManagerDashboardGuard>
      ),
      layout: true,
    },

    { path: "/assigned-users", element: <AssignedUsers />, layout: true },
    { path: "/assigned-users/assigned-detail-view", element: <AssignedDetailView />, layout: true },
    { path: "/communication", element: <Communication />, layout: true },
    { path: "/communication/chats-detail-view", element: <ChatsDetailView />, layout: true },
    { path: "/communication/queries-detail-view", element: <QueriesDetailView />, layout: true },
    { path: "/manager-housing", element: <ManagerHousing />, layout: true },
    { path: "/service-manager", element: <ServiceManager />, layout: true },
    { path: "/service-manager/service-detail-view/:id", element: <ServiceManagerDetailView />, layout: true },

    {
      path: "/manager-onboarding-steps",
      element: (
        <OnboardingGuard>
          <ManagerOnboardSteps />
        </OnboardingGuard>
      ),
      layout: false,
    },

    // Manager Vendor
    { path: "/manager-vendors", element: <ManagerVendors />, layout: true },
    { path: "/manager-vendors/manager-vendors-detail-view", element: <ManagerVendorDetailView />, layout: true },

    //Manager Profile
    { path: "/manager-profile", element: <MyProfile />, layout: true },
  ];

  const commonRoutes = [
    { path: "/", element: <Login />, layout: false },
    { path: "/reset-password", element: <ForgetPasswordInput />, layout: false },
  ];

  // Not Logged In
  if (!isLoggedIn) {
    return (
      <Suspense fallback={<Loader />}>
        <Routes>
          {commonRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    );
  }

  // Role Based Route Selection
  const roleRoutes = role === 3 ? managerRoutes : adminRoutes;

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* Default Redirect After Login */}
        <Route
          path="/"
          element={
            <Navigate
              to={
                role === 3
                  ? user?.profile_completed
                    ? "/manager-dashboard"
                    : "/manager-onboarding-steps"
                  : "/admin-dashboard"
              }
            />
          }
        />

        <Route path="/reset-password" element={<ForgetPasswordInput />} />

        {/* Role Based Routes */}
        {roleRoutes.map(({ path, element, layout }) => (
          <Route
            key={path}
            path={path}
            element={layout ? <Layout>{element}</Layout> : element}
          />
        ))}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;